import { type RegistrationFlow } from '@ory/client-fetch';
import { Registration } from '@ory/elements-react/theme';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { oryComponentOverrides } from '@apps/web/components/auth/ory_components';
import { useOry } from '@apps/web/hooks/ory';

export const Route = createFileRoute('/_auth/sign-up')({
    head: () => ({
        meta: [
            {
                title: 'Sign Up | Phoebe',
            },
        ],
    }),
    beforeLoad: async ({ context }) => {
        const { sessionState } = context;
        if (sessionState?.session) {
            throw redirect({ to: '/' });
        }
    },
    component: SignUpPage,
    validateSearch: z.object({
        flow: z.string().optional(),
        invitation_token: z.string().optional(),
        email: z.string().optional(),
        first_name: z.string().optional(),
        last_name: z.string().optional(),
    }),
});

function SignUpPage() {
    const {
        flow: flowId,
        invitation_token,
        email,
        first_name,
        last_name,
    } = Route.useSearch();
    const router = useRouter();
    const [flow, setFlow] = useState<RegistrationFlow | null>(null);
    const { oryClient, oryElementsConfiguration, normalizeOryPayload } =
        useOry();

    useEffect(() => {
        (async () => {
            try {
                if (flowId) {
                    const flow = normalizeOryPayload(
                        await oryClient.getRegistrationFlow({
                            id: flowId,
                        }),
                    );
                    setFlow(flow);
                } else {
                    const flow = normalizeOryPayload(
                        await oryClient.createBrowserRegistrationFlow(),
                    );
                    router.navigate({
                        to: '/sign-up',
                        search: (prev) => ({ ...prev, flow: flow.id }),
                    });
                }
            } catch (error) {
                console.error(error);
            }
        })();
    }, [flowId, normalizeOryPayload, oryClient, router]);

    if (!flow) {
        return null;
    }

    // Pre-fill the form if we have invitation data
    if (flow.ui.nodes) {
        if (email) {
            const emailNode = flow.ui.nodes.find(
                (node) =>
                    'name' in node.attributes &&
                    node.attributes.name === 'traits.email',
            );
            if (
                emailNode &&
                'value' in emailNode.attributes &&
                !emailNode.attributes.value
            ) {
                emailNode.attributes.value = email;
            }
        }

        if (first_name) {
            const firstNameNode = flow.ui.nodes.find(
                (node) =>
                    'name' in node.attributes &&
                    node.attributes.name === 'traits.first_name',
            );
            if (
                firstNameNode &&
                'value' in firstNameNode.attributes &&
                !firstNameNode.attributes.value
            ) {
                firstNameNode.attributes.value = first_name;
            }
        }

        if (last_name) {
            const lastNameNode = flow.ui.nodes.find(
                (node) =>
                    'name' in node.attributes &&
                    node.attributes.name === 'traits.last_name',
            );
            if (
                lastNameNode &&
                'value' in lastNameNode.attributes &&
                !lastNameNode.attributes.value
            ) {
                lastNameNode.attributes.value = last_name;
            }
        }
    }

    return (
        <div className="ory-root w-full max-w-xl">
            {invitation_token && (
                <div className="mb-4 rounded-md bg-blue-50 p-4 text-sm text-blue-800 dark:bg-blue-950/50 dark:text-blue-300">
                    Create your account to accept the invitation. You’ll
                    automatically join the organization after signing up.
                </div>
            )}
            <Registration
                flow={flow}
                config={oryElementsConfiguration}
                components={oryComponentOverrides}
            />
        </div>
    );
}
